import * as React from 'react';
import { Download, Copy, Check, AlertTriangle } from 'lucide-react';
import { Button } from './button';

export interface BackupCodesViewerProps {
  codes: readonly string[];
  /** Quando true mostra checkbox "Ho salvato i codici" e richiede ack. */
  requireAck?: boolean;
  acknowledged?: boolean;
  onAcknowledgeChange?: (next: boolean) => void;
  /** Nome file scaricato (default `backup-codes.txt`). */
  fileName?: string;
  className?: string;
}

export const BackupCodesViewer: React.FC<BackupCodesViewerProps> = ({
  codes,
  requireAck = false,
  acknowledged = false,
  onAcknowledgeChange,
  fileName = 'backup-codes.txt',
  className,
}) => {
  const [copied, setCopied] = React.useState<boolean>(false);

  const copyAll = React.useCallback(async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(codes.join('\n'));
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable */
    }
  }, [codes]);

  const downloadTxt = React.useCallback((): void => {
    const header = [
      '# Backup codes 2FA (master admin) — EasyFlow',
      `# Generato il ${new Date().toISOString()}`,
      '# Conservali in un luogo sicuro: ogni codice è usabile una sola volta.',
      '',
    ].join('\n');
    const blob = new Blob([header + codes.join('\n') + '\n'], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [codes, fileName]);

  return (
    <div className={`space-y-3 ${className ?? ''}`}>
      <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-500/10 dark:border-amber-500/30 p-3">
        <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
        <p className="text-xs text-amber-800 dark:text-amber-200">
          Salva questi codici subito: non li potrai più rivedere. Ogni codice funziona una sola volta
          e serve a ripristinare l'accesso se perdi l'app TOTP.
        </p>
      </div>
      <div className="rounded-lg border border-border bg-muted/40 dark:bg-white/5 p-4">
        <div className="grid grid-cols-2 gap-x-6 gap-y-1 font-mono text-sm text-foreground">
          {codes.map((c, i) => (
            <div key={i} className="select-all tabular-nums">{c}</div>
          ))}
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button type="button" variant="outline" size="sm" onClick={() => { void copyAll(); }}>
          {copied ? <Check className="h-4 w-4 mr-2 text-green-600" /> : <Copy className="h-4 w-4 mr-2" />}
          {copied ? 'Copiato' : 'Copia tutti'}
        </Button>
        <Button type="button" variant="outline" size="sm" onClick={downloadTxt}>
          <Download className="h-4 w-4 mr-2" />
          Scarica .txt
        </Button>
      </div>
      {requireAck && (
        <label className="flex items-start gap-2 text-sm text-foreground cursor-pointer select-none">
          <input
            type="checkbox"
            checked={acknowledged}
            onChange={(e) => onAcknowledgeChange?.(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-border accent-purple-600"
          />
          <span>Ho salvato i codici in un luogo sicuro</span>
        </label>
      )}
    </div>
  );
};
